import React, { useEffect, useState } from "react";
import ApiConfig from "../common/config/ApiConfig";
import { motion } from "framer-motion";
import moment from "moment";
import "./Lottoresult.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoadingComponent from "./LoadingComponent";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CalculateIcon from "@mui/icons-material/Calculate";
import { useTranslation } from "react-i18next";

// "run_top_front_big" -> "runTopFrontBig", to look up lottoResult.betTypes.*
const toCamelCase = (code) =>
  code.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

function ResultCard({ group, result }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl overflow-hidden border border-brand-blue-100 shadow-sm bg-white">
      <div className="bg-gradient-to-r from-brand-blue-600 to-brand-blue-800 px-3 py-1.5">
        <span className="text-brand-gold-200 font-bold text-xs">{group.name}</span>
      </div>
      <div className="flex flex-col divide-y divide-slate-100">
        {group.rewardTypes.map((rewardType) => {
          const match = result.find((row) => row.type === rewardType.code);
          // big/small/odd/even (words-stake) rows store their outcome as
          // the literal string "true"/"false" in result_number, with the
          // actual evaluated digit in current_result.
          const isBoolResult =
            match?.result_number === "true" ||
            match?.result_number === "false";

          return (
            <div
              key={rewardType.code}
              className="flex items-center justify-between px-3 py-1.5"
            >
              <span className="text-slate-500 text-xs">
                {t(`lottoResult.betTypes.${toCamelCase(rewardType.code)}`, {
                  defaultValue: rewardType.name,
                })}
              </span>
              {!match ? (
                <span className="text-brand-blue-500 font-bold text-sm">
                  {t("lottoResult.pendingResult")}
                </span>
              ) : match.result_number === "calculate" ? (
                <CalculateIcon
                  className="text-amber-500 animate-pulse"
                  fontSize="small"
                />
              ) : isBoolResult ? (
                <div className="flex items-center gap-1.5">
                  {match.current_result !== null &&
                    match.current_result !== undefined && (
                      <span className="text-brand-gold-600 font-bold text-sm">
                        {match.current_result}
                      </span>
                    )}
                  {match.result_number === "true" ? (
                    <CheckCircleIcon
                      className="text-emerald-500"
                      fontSize="small"
                    />
                  ) : (
                    <CancelIcon className="text-rose-500" fontSize="small" />
                  )}
                </div>
              ) : (
                <span className="text-brand-gold-600 font-bold text-sm">
                  {match.result_number}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Lottoresult() {
  const { t } = useTranslation();
  const [result, setResult] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const [lottotype, setLottotype] = useState("th_gov");
  const [startDate, setStartDate] = useState(new Date());
  const [round, setRound] = useState(1);
  const [lottoTypes, setLottoTypes] = useState([]);
  const [rewardTypeGroups, setRewardTypeGroups] = useState([]);

  useEffect(() => {
    ApiConfig.get("/getLottoTypeList").then((res) => {
      if (res.resCode === 200) setLottoTypes(res.data);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    ApiConfig.get("/getLottoRewardTypeGroups").then((res) => {
      if (res.resCode === 200) setRewardTypeGroups(res.data);
    });
  }, []);

  // Only groups belonging to the currently selected lotto type, and only
  // the ones actually open for it (e.g. "4 ตัว" is disabled for th_gov).
  const groupsForType = rewardTypeGroups.filter(
    (group) => group.lottoType?.type === lottotype && group.status,
  );

  // The single 6-digit "รางวัลที่ 1" group gets its own hero card with big
  // digit boxes; everything else renders as a compact list card.
  const heroGroup = groupsForType.find(
    (group) =>
      group.rewardTypes.length === 1 && group.rewardTypes[0].digit === 6,
  );
  const listGroups = groupsForType.filter((group) => group !== heroGroup);
  const heroResult = heroGroup
    ? result.find((row) => row.type === heroGroup.rewardTypes[0].code)
    : undefined;

  const currentType = lottoTypes.find((item) => item.type === lottotype);

  const ResGetbydate = () => {
    const params = new URLSearchParams({
      date: moment(startDate).format("YYYY-MM-DD"),
      lottotype: lottotype,
      round: Number(round),
    });
    ApiConfig.get(`/getresultbylottotype?${params.toString()}`).then(
      (result) => {
        if (result.resCode === 200) setResult(result.data);
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    setLoading(true);
    ResGetbydate();
    // eslint-disable-next-line
  }, [startDate, lottotype, round]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col w-[100%] h-[auto] justify-center"
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div className="flex flex-col justify-center">
          <div className="w-[100%] flex flex-row items-center h-[44px] p-2 px-4 bg-gradient-to-r from-brand-blue-800 via-brand-blue-700 to-brand-blue-800 border-b border-brand-gold-300/40 space-x-2 rounded-t-lg shadow-card">
            <span className="text-brand-gold-200 font-bold font-heading text-m tracking-wide">
              {t("lottoResult.header")}
            </span>
          </div>
          <div className="flex flex-row justify-center bg-gradient-to-br from-brand-blue-50 via-white to-brand-blue-100 p-3 w-[100%] h-[auto] shadow-inner">
            <div className="md:flex flex-col w-[100%] flex-row bg-white rounded-xl shadow-md overflow-hidden">
              <div className="flex flex-row flex-wrap items-end bg-gradient-to-r from-brand-blue-800 to-brand-blue-900 p-3 gap-4">
                <div>
                  <h3 className="flex items-center gap-1 text-brand-gold-200 text-xs font-semibold mb-1">
                    <CalendarTodayIcon fontSize="inherit" />
                    {t("lottoResult.dateLabel")}
                  </h3>
                  <DatePicker
                    className="text-brand-blue-900 font-semibold rounded-lg md:w-[100%] w-[100px] px-2 py-1.5 shadow-sm border-0"
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                  />
                </div>

                <div>
                  <h3 className="flex items-center gap-1 text-brand-gold-200 text-xs font-semibold mb-1">
                    <ConfirmationNumberIcon fontSize="inherit" />
                    {t("lottoResult.typeLabel")}
                  </h3>
                  <select
                    className="text-brand-blue-900 font-semibold rounded-lg px-2 py-1.5 shadow-sm border-0"
                    value={lottotype}
                    onInput={(e) => {
                      setLottotype(e.target.value);
                      setRound(1);
                    }}
                  >
                    {lottoTypes.map((item) => (
                      <option key={item.id} value={item.type}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <h3 className="text-brand-gold-200 text-xs font-semibold mb-1">
                    {t("lottoResult.roundLabel")}
                  </h3>
                  <select
                    className="text-brand-blue-900 font-semibold rounded-lg px-2 py-1.5 shadow-sm border-0"
                    value={round}
                    onInput={(e) => setRound(e.target.value)}
                  >
                    {currentType?.rounds?.map((r) => (
                      <option key={r.id} value={r.round}>
                        {r.round}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => {
                    setLoading(true);
                    ResGetbydate();
                  }}
                  className="flex items-center gap-1 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg px-3 py-1.5 shadow-sm transition-colors"
                >
                  <ReplayIcon fontSize="inherit" />
                  {t("lottoResult.refresh")}
                </button>
              </div>

              <div className="p-3">
                {loading ? (
                  <LoadingComponent />
                ) : result.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-10 text-slate-400">
                    <SearchOffIcon fontSize="large" />
                    <span className="font-semibold text-sm">
                      {t("lottoResult.noResult")}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {heroGroup && (
                      <div className="rounded-xl overflow-hidden border border-brand-gold-300/50 shadow-sm">
                        <div className="flex items-center justify-between bg-gradient-to-r from-brand-blue-700 to-brand-blue-900 px-4 py-2">
                          <span className="text-brand-gold-200 font-bold text-sm">
                            {heroGroup.name}
                          </span>
                          {heroResult?.date && (
                            <span className="text-brand-blue-100 text-xs font-semibold">
                              {moment(heroResult.date).format("ll")}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-center gap-2 py-5 bg-white">
                          {heroResult?.result_number ? (
                            heroResult.result_number
                              .split("")
                              .map((digit, i) => (
                                <span
                                  key={i}
                                  className="flex items-center justify-center w-9 h-11 md:w-12 md:h-14 text-brand-ink-onGold text-xl md:text-2xl font-extrabold rounded-lg bg-gradient-to-b from-brand-gold-300 to-brand-gold-600 border border-brand-gold-200 shadow-gold"
                                >
                                  {digit}
                                </span>
                              ))
                          ) : (
                            <span className="text-slate-400 text-sm">
                              {t("lottoResult.pendingResult")}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {listGroups.map((group) => (
                        <ResultCard
                          key={group.id}
                          group={group}
                          result={result}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default Lottoresult;
